import { ArrowDown2 } from 'iconsax-react';
import { useRef } from 'react';
import { type Control, Controller, type FieldPath, type FieldValues, useController } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useOnClickOutside } from 'usehooks-ts';
import useCountryList from '@/hooks/settings/use-country-list';
import cn from '@/utils/merge-class-names';

interface Props<TFieldValues extends FieldValues> {
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  names: [FieldPath<TFieldValues>, FieldPath<TFieldValues>];
  control: Control<TFieldValues>;
}

export function PhoneField<TF extends FieldValues = Record<string, never>>({
  required = false,
  disabled = false,
  placeholder,
  names,
  control,
}: Props<TF>) {
  const detailRef = useRef<HTMLDetailsElement>(null);
  const { t } = useTranslation();
  const { countryList } = useCountryList();
  const {
    field: { value: selectedTelCode, onChange: setSelectedTelCode },
  } = useController({ control, name: names[0] });

  useOnClickOutside(detailRef, () => detailRef.current?.removeAttribute('open'));

  const handleSelect = (telCode: string) => {
    setSelectedTelCode(telCode);
    detailRef.current?.removeAttribute('open');
  };

  return (
    <div className='flex gap-1.5'>
      <Controller
        control={control}
        render={({ field }) => (
          <details
            ref={detailRef}
            data-testid='country-code-selector'
            className={cn('dropdown', { 'pointer-events-none': disabled })}>
            <summary
              aria-disabled={disabled}
              className={cn(
                'flex h-12 w-[5.625rem] items-center justify-evenly rounded border bg-neutral-100 px-2 leading-[2.875rem]',
                { 'input-disabled bg-neutral-200': disabled },
              )}>
              <span className='w-[3.125rem]'>{selectedTelCode ? `+${selectedTelCode}` : ''}</span>
              <ArrowDown2
                className='text-metal-700'
                size={14}
              />
            </summary>
            <ul className='dropdown-content z-10 mt-1 max-h-[13.75rem] w-44 overflow-y-auto rounded-lg bg-neutral-0 p-2 shadow-md'>
              {countryList?.map(({ ID, TelCode }) => (
                <li
                  data-testid={`country-code-${TelCode}`}
                  key={ID}
                  className={cn('px-4 py-2.5 text-neutral-800 cursor-pointer', {
                    'rounded-lg bg-secondary': selectedTelCode === TelCode,
                  })}
                  onClick={() => {
                    field.onChange(TelCode);
                    handleSelect(TelCode);
                  }}>
                  {`${t(`country-codes.${TelCode}`)}+${TelCode}`}
                </li>
              ))}
            </ul>
          </details>
        )}
        name={names[0]}
      />

      <Controller
        control={control}
        render={({ field }) => (
          <input
            type='text'
            inputMode='numeric'
            data-testid='phone-number-input'
            disabled={disabled}
            className='input input-bordered w-full disabled:input-disabled'
            placeholder={placeholder}
            autoComplete='tel-local'
            required={required}
            {...field}
            onChange={(e) => {
              const newValue = e.target.value.replace(/[^0-9]/g, '');
              field.onChange(newValue);
            }}
          />
        )}
        name={names[1]}
      />
    </div>
  );
}
